import { parseISO, isValid, format } from 'date-fns';

export function getNewFormattedDate(): string {
  return formatDate(new Date());
}

/**
 * Formats a given ISO-8601 string date into a full date format based on the user's locale.
 * Example (US locale): "2025-10-01" -> "October 01, 2025"
 * @param isoDateString - The date string in ISO-8601 format.
 * @returns The formatted full date string.
 */
export function getFormattedFullDateFromIsoString(
  isoDateString: string,
): string {
  if (!isValidIsoDateString(isoDateString)) return '';

  const date = parseDateTime(isoDateString);
  const formatter = new Intl.DateTimeFormat(
    Intl.DateTimeFormat().resolvedOptions().locale,
    {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

  return formatter.format(date);
}

/**
 * Formats a given ISO-8601 string date into a short date format based on the user's locale.
 * Example (US locale): "2025-10-01" -> "10/01/2025"
 * @param isoDateString - The date string in ISO-8601 format.
 * @returns The formatted short date string.
 */
export function getFormattedDateFromIsoString(isoDateString: string): string {
  if (!isValidIsoDateString(isoDateString)) return '';

  const date = parseDateTime(isoDateString);
  return formatDate(date);
}

const ORG_DATE_FORMAT_TO_DATE_FNS: Record<string, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'YY/MM/DD': 'yy/MM/dd',
  'DD.MM.YYYY': 'dd.MM.yyyy',
};

export function getFormattedDateFromIsoStringWithFormat(
  isoDateString: string,
  dateFormat: string | null | undefined,
): string {
  if (!isValidIsoDateString(isoDateString)) return '';
  const date = parseDateTime(isoDateString);
  const dateFnsFormat =
    (dateFormat && ORG_DATE_FORMAT_TO_DATE_FNS[dateFormat]) ?? 'MM/dd/yyyy';
  return format(date, dateFnsFormat);
}

/**
 * Formats a given ISO-8601 string into a 'hh:MM a' time format, adjusting for the user's timezone.
 * Example: "2023-10-01T14:30:00.000Z" (UTC) -> "9:30 AM" (America/Toronto, UTC-5)
 * @param isoTimeString - The datetime string in ISO-8601 format.
 * @returns The formatted time string in the user's timezone.
 */
export function getFormattedTimeFromIsoString(isoTimeString: string): string {
  if (!isValidIsoDateString(isoTimeString)) return '';

  const date = parseDateTime(isoTimeString);

  const formatter = new Intl.DateTimeFormat(
    Intl.DateTimeFormat().resolvedOptions().locale,
    {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

  return formatter.format(date);
}

/**
 * Formats a given ISO-8601 string into a human-readable date and time format.
 * Example: "2025-01-12T15:30:00.000Z" -> "Jan 12 at 3:30PM"
 */
export function getHumanReadableDateTimeFromIsoString(
  isoDateString: string,
): string {
  if (!isValidIsoDateString(isoDateString)) return '';

  const date = parseDateTime(isoDateString);
  const { locale, timeZone } = Intl.DateTimeFormat().resolvedOptions();

  const formattedDate = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(date);

  const formattedTime = getFormattedTimeFromIsoString(isoDateString).replace(
    ' ',
    '',
  );

  return `${formattedDate} at ${formattedTime}`;
}

export function formatDate(date: Date): string {
  const formatter = getDateFormatter();
  return formatter.format(date);
}

export function getLocalDateFormat() {
  const formatter = getDateFormatter();
  const parts = formatter.formatToParts(new Date());
  const separator = parts.find((part) => part.type === 'literal')?.value || '/';

  const formatOrder = parts
    .filter((part) => ['day', 'month', 'year'].includes(part.type))
    .map((part) => {
      if (part.type === 'year') return 'YYYY';
      if (part.type === 'month') return 'MM';
      if (part.type === 'day') return 'DD';
    })
    .join(separator);

  return formatOrder;
}

/**
 * Returns true if the string starts with a full YYYY-MM-DD date prefix.
 * Guards against truncated ISO-8601 formats like "2025" (year-only) or "2025-03"
 * (year-month), which parseISO accepts as valid but should not be treated as dates.
 */
export function isFullIsoDateString(dateString: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}/.test(dateString) && isValidIsoDateString(dateString)
  );
}

/**
 * Returns true if the string is a full ISO datetime with a time component
 * (e.g. "2026-05-06T12:00:00+00:00"). Manage sync always writes this format,
 * so this is used to detect date fields by value when type metadata is unavailable.
 */
export function isIsoDateTimeString(dateString: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T/.test(dateString ?? '');
}

export function isValidIsoDateString(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }
  const parsed = parseISO(dateString);
  return isValid(parsed);
}

function parseDateTime(stringDate: string): Date {
  return parseISO(stringDate);
}

function getDateFormatter(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(
    Intl.DateTimeFormat().resolvedOptions().locale,
    {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );
}

/**
 * Formats a date string as a relative time string (e.g. 'Just now', '3 hours ago', '5 days ago').
 * Falls back to toLocaleDateString() for dates older than 7 days.
 * @param dateString - A date string parseable by new Date().
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

/**
 * Formats a date string as a long month name + day with no year (e.g. 'October 1').
 * Used for human-readable dates where the year is implied by context.
 * @param dateString - A date string parseable by new Date().
 */
export function formatMonthDayFromString(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
}
